/**
 * Tenants Redux Slice
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  Tenant,
  TenantsState,
  TenantsFilter,
  TenantStats,
  TenantsListResponse,
  TenantStatus,
  TenantChannel,
} from '../domain/types/tenant.types'

const initialState: TenantsState = {
  items: [],
  selectedTenant: null,
  filter: { status: 'all', channel: 'all', search: '', page: 1, pageSize: 10 },
  pagination: { page: 1, pageSize: 10, pageCount: 1, total: 0 },
  stats: { total: 0, pending: 0, active: 0, suspended: 0 },
  isLoading: false,
  error: null,
  customerGroups: [],
  isCustomerGroupsLoading: false,
  customerGroupsError: null,
}

export const tenantsSlice = createSlice({
  name: 'tenants',
  initialState,
  reducers: {
    // Fetch
    fetchTenants: (state, _action: PayloadAction<TenantsFilter | undefined>) => {
      state.isLoading = true
      state.error = null
    },
    fetchTenantsSuccess: (state, action: PayloadAction<TenantsListResponse>) => {
      state.items = action.payload.data
      state.pagination = action.payload.meta
      if (action.payload.globalStats) {
        state.stats = action.payload.globalStats
      }
      state.isLoading = false
      state.error = null
    },
    fetchTenantsFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },

    // Selection
    selectTenant: (state, action: PayloadAction<Tenant | null>) => {
      state.selectedTenant = action.payload
    },

    // Filters
    setFilter: (state, action: PayloadAction<Partial<TenantsFilter>>) => {
      state.filter = { ...state.filter, ...action.payload }
    },
    clearFilters: (state) => {
      state.filter = { status: 'all', channel: 'all', search: '', page: 1, pageSize: 10 }
    },

    // Pagination
    setPage: (state, action: PayloadAction<number>) => {
      state.filter.page = action.payload
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.filter.pageSize = action.payload
      state.filter.page = 1 // Reset to first page when changing page size
    },

    // Filter shortcuts
    filterByStatus: (state, action: PayloadAction<TenantStatus | 'all'>) => {
      state.filter.status = action.payload
      state.filter.page = 1
    },
    filterByChannel: (state, action: PayloadAction<TenantChannel | 'all'>) => {
      state.filter.channel = action.payload
      state.filter.page = 1
    },
    searchTenants: (state, action: PayloadAction<string>) => {
      state.filter.search = action.payload
      state.filter.page = 1
    },

    // Stats
    setStats: (state, action: PayloadAction<TenantStats>) => {
      state.stats = action.payload
    },

    // Approve tenant
    approveTenant: (
      state,
      _action: PayloadAction<{
        tenantId: string
        unblockUsers?: boolean
        customerGroupDocumentId?: string
        sapCustomerGroup?: string
      }>
    ) => {
      state.isLoading = true
    },
    approveTenantSuccess: (state, action: PayloadAction<Tenant>) => {
      state.isLoading = false
      const index = state.items.findIndex(t => t.id === action.payload.id)
      if (index !== -1) state.items[index] = action.payload
      if (state.selectedTenant?.id === action.payload.id) {
        state.selectedTenant = action.payload
      }
    },

    // Suspend tenant
    suspendTenant: (state, _action: PayloadAction<{ tenantId: string }>) => {
      state.isLoading = true
    },
    suspendTenantSuccess: (state, action: PayloadAction<Tenant>) => {
      state.isLoading = false
      const index = state.items.findIndex(t => t.id === action.payload.id)
      if (index !== -1) state.items[index] = action.payload
      if (state.selectedTenant?.id === action.payload.id) {
        state.selectedTenant = action.payload
      }
    },

    // Fetch customer groups (for approval UI)
    fetchCustomerGroups: (state) => {
      state.isCustomerGroupsLoading = true
      state.customerGroupsError = null
    },
    fetchCustomerGroupsSuccess: (state, action: PayloadAction<TenantsState['customerGroups']>) => {
      state.isCustomerGroupsLoading = false
      state.customerGroups = action.payload
    },
    fetchCustomerGroupsFailure: (state, action: PayloadAction<string>) => {
      state.isCustomerGroupsLoading = false
      state.customerGroupsError = action.payload
    },

    // Update tenant
    updateTenant: (state, _action: PayloadAction<{ tenantId: string; data: Partial<Tenant>; customerGroupId?: string }>) => {
      state.isLoading = true
    },
    updateTenantSuccess: (state, action: PayloadAction<Tenant>) => {
      state.isLoading = false
      const index = state.items.findIndex(t => t.id === action.payload.id)
      if (index !== -1) state.items[index] = action.payload
      if (state.selectedTenant?.id === action.payload.id) {
        state.selectedTenant = action.payload
      }
    },

    // Create tenant
    createTenant: (state, _action: PayloadAction<Omit<Tenant, 'id' | 'documentId' | 'createdAt' | 'updatedAt'>>) => {
      state.isLoading = true
    },
    createTenantSuccess: (state, action: PayloadAction<Tenant>) => {
      state.isLoading = false
      state.items.push(action.payload)
    },

    // Delete tenant
    deleteTenant: (state, _action: PayloadAction<{ tenantId: string }>) => {
      state.isLoading = true
    },
    deleteTenantSuccess: (state, action: PayloadAction<string>) => {
      state.isLoading = false
      state.items = state.items.filter(t => t.id !== action.payload)
      if (state.selectedTenant?.id === action.payload) {
        state.selectedTenant = null
      }
    },

    // General action failure
    actionFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },

    // Clear state
    clearTenants: (state) => {
      state.items = []
      state.selectedTenant = null
      state.error = null
    },
  },
})

export const tenantsReducer = tenantsSlice.reducer
export const tenantsActions = tenantsSlice.actions
